import React, { useEffect, useMemo, useRef, useState } from "react";

// Lightweight searchable dropdown. Items: [{ value, label }]
export default function SearchableSelect({
  items = [],
  value,
  onChange,
  placeholder = "Search...",
  disabled = false,
  emptyText = "No results",
  maxItems = 300,
}) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const containerRef = useRef(null);

  const selectedLabel = useMemo(() => {
    const found = items.find((it) => String(it.value) === String(value));
    return found ? found.label : "";
  }, [items, value]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return items.slice(0, maxItems);
    return items
      .filter((it) => {
        const v = String(it.value ?? "").toLowerCase();
        const l = String(it.label ?? "").toLowerCase();
        return v.includes(q) || l.includes(q);
      })
      .slice(0, maxItems);
  }, [items, query, maxItems]);

  useEffect(() => {
    function onDocClick(e) {
      if (!containerRef.current) return;
      if (!containerRef.current.contains(e.target)) {
        setOpen(false);
      }
    }
    if (open) {
      document.addEventListener("mousedown", onDocClick);
    }
    return () => document.removeEventListener("mousedown", onDocClick);
  }, [open]);

  function handleSelect(val) {
    onChange?.(val);
    setOpen(false);
  }

  return (
    <div ref={containerRef} style={{ position: "relative" }}>
      <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
        <input
          type="text"
          placeholder={placeholder}
          value={open ? query : selectedLabel || ""}
          onChange={(e) => setQuery(e.target.value)}
          onFocus={() => setOpen(true)}
          disabled={disabled}
          style={{
            width: "100%",
            padding: "6px 8px",
            background: "#ffffff",
            color: "#000",
            border: "1px solid #ccc",
            borderRadius: 4,
          }}
        />
        <button
          type="button"
          disabled={disabled}
          onClick={() => setOpen((v) => !v)}
          style={{
            background: "#ffffff",
            color: "#000",
            border: "1px solid #ccc",
            borderRadius: 4,
            padding: "6px 8px",
            cursor: disabled ? "not-allowed" : "pointer",
          }}
          aria-label={open ? "Close" : "Open"}
        >
          ▾
        </button>
      </div>
      {open && !disabled && (
        <div
          style={{
            position: "absolute",
            zIndex: 10,
            marginTop: 4,
            left: 0,
            right: 0,
            maxHeight: 240,
            overflow: "auto",
            border: "1px solid #ccc",
            background: "#fff",
            color: "#000",
            borderRadius: 4,
            boxShadow: "0 2px 6px rgba(0,0,0,0.15)",
          }}
          role="listbox"
        >
          {filtered.length === 0 && (
            <div style={{ padding: 8, color: "#555" }}>{emptyText}</div>
          )}
          {filtered.map((it) => (
            <div
              key={String(it.value)}
              role="option"
              aria-selected={String(value) === String(it.value)}
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => handleSelect(it.value)}
              style={{
                padding: "6px 8px",
                cursor: "pointer",
                background:
                  String(value) === String(it.value) ? "#e6f0ff" : "#fff",
              }}
            >
              {it.label}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
